/* eslint-disable react/display-name */
import React, { useState, useMemo } from 'react';
import { CustomLabel } from 'modules/timesheet-labels/labelSchema';
import { SelectLabelOption } from 'graphql/types';
import { LabelValueType } from '../LabelFacetDetails';
import { SearchBoxProps } from '../type';
import {
  GetCustomLabelOptionsType,
  useGetCustomLabelOptions
} from './hooks/useGetCustomLabelOptions';
import { customLabelScoredSuggestions } from './customLabelScoredSuggestions';

export const cleanSearchTerm = (value: string): string =>
  value.replace(/['",]+/g, '');

const mapOption = (option: CustomLabel): LabelValueType => ({
  value: option.id,
  label: option.name,
  key: option.name,
  displayText: option.name,
  labelId: option.id
});

const matchOption = (
  fetchCustomLabelOptions: GetCustomLabelOptionsType
) => async (
  customLabels: CustomLabel[],
  term: string
): Promise<LabelValueType | null> => {
  if (!term) return null;

  const [labelKey, ...values] = term.split('+');
  const filteredCustomLabel = customLabels
    .map(mapOption)
    .filter((option) => option.key === labelKey);
  const customLabel = filteredCustomLabel.length > 0 && filteredCustomLabel[0];
  if (customLabel && customLabel.key) {
    const results = await Promise.all(
      values.map((value) => fetchCustomLabelOptions(customLabel.labelId, value))
    );
    const searchValues = values.map((v) => v.toLocaleLowerCase());
    const uniqueResults = results
      .flat()
      .filter((v) => v && searchValues.includes(v.name.toLocaleLowerCase()))
      .reduce((acc, opt) => {
        acc[opt.id] = opt;
        return acc;
      }, {} as Record<string, SelectLabelOption>);
    customLabel.values = Object.values(uniqueResults);
    customLabel.key = `${customLabel.label}+${customLabel.values
      .map((v) => v.name)
      .join('+')}`;
    return customLabel;
  }

  return null;
};

const withCriteriaProvider = (
  tag: string,
  context: React.Context<CustomLabelCriteriaProviderContextType>,
  customLabels: CustomLabel[]
) => (
  BaseComponent: React.FunctionComponent<SearchBoxProps>
): React.FC<SearchBoxProps> => (props: SearchBoxProps) => {
  const { searchCriteria: { criterions = {} } = {} } = props;
  const [selected, setSelected] = useState<LabelValueType[]>(
    (criterions[tag] as LabelValueType[]) || []
  );

  const fetchCustomLabelOptions = useGetCustomLabelOptions();
  const value = useMemo(
    () => ({
      selected,
      setSelected,
      options: customLabels,
      matchOption: matchOption(fetchCustomLabelOptions),
      suggestions: customLabelScoredSuggestions(
        customLabels,
        fetchCustomLabelOptions
      )
    }),
    [fetchCustomLabelOptions, selected]
  );

  return (
    <context.Provider value={value}>
      <BaseComponent {...props} />
    </context.Provider>
  );
};

type CustomLabelCriteriaProviderContextType = {
  selected: LabelValueType[];
  setSelected: React.Dispatch<React.SetStateAction<LabelValueType[]>>;
  matchOption: (
    options: CustomLabel[],
    term: string
  ) => Promise<LabelValueType | null>;
};

const makeCriteriaProvider = (customLabels: CustomLabel[]) => (
  tag: string,
  context: React.Context<CustomLabelCriteriaProviderContextType>
): ((
  BaseComponent: React.FunctionComponent<SearchBoxProps>
) => React.FC<SearchBoxProps>) =>
  withCriteriaProvider(tag, context, customLabels);

export default makeCriteriaProvider;
